/**
 * Staff Payroll Config card — Manager/Admin-only edit surface for staff pay.
 *
 * Fields: contracted hours, base rate, overtime (method + multiplier / rate +
 * threshold), weekend + bank-holiday enhancements (multiplier OR flat uplift),
 * sleep-in flat rate, default unpaid break, payroll reference.
 *
 * Server enforces the actual permission — this card renders a read-only
 * summary for Care Workers and hides pay fields entirely.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BadgePoundSterling, Save } from "lucide-react";

const DEFAULTS = {
  contracted_hours_week: 40,
  base_rate: 12,
  overtime_method: "none",
  overtime_multiplier: null,
  overtime_rate: null,
  overtime_threshold: null,
  weekend_method: "none",
  weekend_multiplier: null,
  weekend_uplift: null,
  bank_hol_method: "none",
  bank_hol_multiplier: null,
  bank_hol_uplift: null,
  sleep_in_rate: 0,
  unpaid_break_min: 30,
  payroll_reference: "",
};

export default function PayrollConfigCard({ staffId, canManage }) {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/payroll/config/${staffId}`);
      setForm({ ...DEFAULTS, ...(r.data || {}) });
    } catch (e) {
      // 403 for a care worker viewing someone else — hide the card
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (form.overtime_method === "multiplier" && !(form.overtime_multiplier > 0)) {
      toast.error("Overtime multiplier must be > 0");
      return;
    }
    if (form.overtime_method === "rate" && !(form.overtime_rate > 0)) {
      toast.error("Overtime rate must be > 0");
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => v !== "" && v !== undefined),
      );
      await api.put(`/payroll/config/${staffId}`, { data: payload });
      toast.success("Payroll config saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="payroll-config-loading">Loading payroll config…</div>;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="payroll-config-card">
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <BadgePoundSterling className="w-3.5 h-3.5" /> Payroll
          </div>
          <h3 className="font-heading text-sm font-semibold">Pay configuration</h3>
        </div>
        {canManage && (
          <Button size="sm" onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-payroll-config-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Contracted hours / week" testid="cfg-contracted">
          <Input type="number" step="0.5" value={form.contracted_hours_week || ""}
                 disabled={!canManage}
                 onChange={(e) => setForm({ ...form, contracted_hours_week: parseFloat(e.target.value) || 0 })}
                 className="h-9 mono text-sm" />
        </Field>
        {canManage && (
          <Field label="Base rate (£/h)" testid="cfg-base-rate">
            <Input type="number" step="0.01" value={form.base_rate || ""}
                   onChange={(e) => setForm({ ...form, base_rate: parseFloat(e.target.value) || 0 })}
                   className="h-9 mono text-sm" />
          </Field>
        )}
        {canManage && (
          <Field label="Payroll reference" testid="cfg-payroll-ref">
            <Input value={form.payroll_reference || ""}
                   onChange={(e) => setForm({ ...form, payroll_reference: e.target.value })}
                   placeholder="e.g. PR-1023" className="h-9 mono text-sm" />
          </Field>
        )}
        {canManage && (
          <Field label="Unpaid break (min / shift)" testid="cfg-break">
            <Input type="number" step="5" value={form.unpaid_break_min ?? ""}
                   onChange={(e) => setForm({ ...form, unpaid_break_min: parseInt(e.target.value, 10) || 0 })}
                   className="h-9 mono text-sm" />
          </Field>
        )}
        {canManage && (
          <Field label="Sleep-in rate (£/shift)" testid="cfg-sleep-in">
            <Input type="number" step="0.01" value={form.sleep_in_rate ?? ""}
                   onChange={(e) => setForm({ ...form, sleep_in_rate: parseFloat(e.target.value) || 0 })}
                   className="h-9 mono text-sm" />
          </Field>
        )}
        {canManage && (
          <Field label="Overtime threshold (h/week)" testid="cfg-ot-threshold" hint="Blank → uses contracted weekly hours">
            <Input type="number" step="0.5" value={form.overtime_threshold ?? ""}
                   onChange={(e) => setForm({ ...form, overtime_threshold: e.target.value ? parseFloat(e.target.value) : null })}
                   className="h-9 mono text-sm" />
          </Field>
        )}

        {canManage && (
          <div className="md:col-span-3 border-t border-slate-100 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Overtime</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Method" testid="cfg-ot-method">
                <Select value={form.overtime_method || "none"} onValueChange={(v) => setForm({ ...form, overtime_method: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="multiplier">Multiplier of base</SelectItem>
                    <SelectItem value="rate">Fixed £/hour</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Multiplier (e.g. 1.5)" testid="cfg-ot-mult">
                <Input type="number" step="0.01" disabled={form.overtime_method !== "multiplier"}
                       value={form.overtime_multiplier ?? ""}
                       onChange={(e) => setForm({ ...form, overtime_multiplier: parseFloat(e.target.value) || null })}
                       className="h-9 mono text-sm" />
              </Field>
              <Field label="Fixed rate (£/h)" testid="cfg-ot-rate">
                <Input type="number" step="0.01" disabled={form.overtime_method !== "rate"}
                       value={form.overtime_rate ?? ""}
                       onChange={(e) => setForm({ ...form, overtime_rate: parseFloat(e.target.value) || null })}
                       className="h-9 mono text-sm" />
              </Field>
            </div>
          </div>
        )}

        {canManage && (
          <>
            <EnhancementRow
              title="Weekend enhancement"
              method={form.weekend_method}
              multiplier={form.weekend_multiplier}
              uplift={form.weekend_uplift}
              onChange={(patch) => setForm({ ...form,
                weekend_method:     patch.method     ?? form.weekend_method,
                weekend_multiplier: patch.multiplier ?? form.weekend_multiplier,
                weekend_uplift:     patch.uplift     ?? form.weekend_uplift })}
              testid="cfg-weekend"
            />
            <EnhancementRow
              title="Bank-holiday enhancement"
              method={form.bank_hol_method}
              multiplier={form.bank_hol_multiplier}
              uplift={form.bank_hol_uplift}
              onChange={(patch) => setForm({ ...form,
                bank_hol_method:     patch.method     ?? form.bank_hol_method,
                bank_hol_multiplier: patch.multiplier ?? form.bank_hol_multiplier,
                bank_hol_uplift:     patch.uplift     ?? form.bank_hol_uplift })}
              testid="cfg-bh"
            />
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function EnhancementRow({ title, method, multiplier, uplift, onChange, testid }) {
  return (
    <div className="md:col-span-3 border-t border-slate-100 pt-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Method">
          <Select value={method || "none"} onValueChange={(v) => onChange({ method: v })}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="multiplier">Multiplier of base</SelectItem>
              <SelectItem value="uplift">Flat £/h uplift</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Multiplier (e.g. 1.25)">
          <Input type="number" step="0.01" disabled={method !== "multiplier"}
                 value={multiplier ?? ""}
                 onChange={(e) => onChange({ multiplier: parseFloat(e.target.value) || null })}
                 className="h-9 mono text-sm" />
        </Field>
        <Field label="Uplift (£/h)">
          <Input type="number" step="0.01" disabled={method !== "uplift"}
                 value={uplift ?? ""}
                 onChange={(e) => onChange({ uplift: parseFloat(e.target.value) || null })}
                 className="h-9 mono text-sm" />
        </Field>
      </div>
    </div>
  );
}
